import React, { useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Plus,
  Loader2,
  Trash2,
  Edit,
  UserCheck,
  XCircle,
  Package,
  ClipboardList,
  ChevronDown,
  ChevronUp,
  GripVertical,
  DollarSign,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatPrice, getPricingModelLabel } from './adminUtils';

const PackagesTab = ({ api, servicePackages, fetchData }) => {
  const [showPackageDialog, setShowPackageDialog] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [creating, setCreating] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [packageForm, setPackageForm] = useState({
    name: '',
    description: '',
    pricing_model: 'flat',
    flat_price_ex_gst: 0,
    variants: [],
    matrix_rows: [],
    checklist_items: [],
    optional_extras: [],
    notes: '',
    require_checklist_completion: true,
    require_item_photos: false,
  });

  const initializePackageForm = (pkg = null) => {
    if (pkg) {
      setPackageForm({
        name: pkg.name || '',
        description: pkg.description || '',
        pricing_model: pkg.pricing_model || 'flat',
        flat_price_ex_gst: pkg.flat_price_ex_gst || 0,
        variants: pkg.variants || [],
        matrix_rows: pkg.matrix_rows || [],
        checklist_items: pkg.checklist_items || [],
        optional_extras: pkg.optional_extras || [],
        notes: pkg.notes || '',
        require_checklist_completion: pkg.require_checklist_completion !== false,
        require_item_photos: pkg.require_item_photos || false,
      });
      setEditingPackage(pkg);
    } else {
      setPackageForm({
        name: '',
        description: '',
        pricing_model: 'flat',
        flat_price_ex_gst: 0,
        variants: [{ id: crypto.randomUUID(), name: '', price_ex_gst: 0, sort_order: 1 }],
        matrix_rows: [],
        checklist_items: [],
        optional_extras: [],
        notes: '',
        require_checklist_completion: true,
        require_item_photos: false,
      });
      setEditingPackage(null);
    }
    setExpandedGroups({});
    setShowPackageDialog(true);
  };

  const handleSavePackage = async () => {
    if (!packageForm.name.trim()) {
      toast.error('Please enter a package name');
      return;
    }
    setCreating(true);
    try {
      const payload = {
        name: packageForm.name,
        description: packageForm.description,
        pricing_model: packageForm.pricing_model,
        notes: packageForm.notes,
        optional_extras: packageForm.optional_extras,
        checklist_items: packageForm.checklist_items,
        require_checklist_completion: packageForm.require_checklist_completion,
        require_item_photos: packageForm.require_item_photos,
      };

      if (packageForm.pricing_model === 'flat') {
        payload.flat_price_ex_gst = parseFloat(packageForm.flat_price_ex_gst) || 0;
      } else if (packageForm.pricing_model === 'variant') {
        payload.variants = packageForm.variants.filter(v => v.name.trim());
      } else if (packageForm.pricing_model === 'matrix') {
        payload.matrix_rows = packageForm.matrix_rows;
      }

      if (editingPackage) {
        await api().patch(`/admin/service-packages/${editingPackage.id}`, payload);
        toast.success('Package updated');
      } else {
        await api().post('/admin/service-packages', payload);
        toast.success('Package created');
      }
      setShowPackageDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save package');
    } finally {
      setCreating(false);
    }
  };

  const handleDeletePackage = async (pkgId) => {
    if (!window.confirm('Are you sure you want to delete this service package?')) return;
    try {
      await api().delete(`/admin/service-packages/${pkgId}`);
      toast.success('Package deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete package');
    }
  };

  const handleTogglePackageActive = async (pkg) => {
    try {
      await api().patch(`/admin/service-packages/${pkg.id}`, { active: !pkg.active });
      toast.success(`Package ${pkg.active ? 'deactivated' : 'activated'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update package');
    }
  };

  // Package form helpers
  const addVariant = () => {
    setPackageForm(prev => ({
      ...prev,
      variants: [...prev.variants, { id: crypto.randomUUID(), name: '', price_ex_gst: 0, sort_order: prev.variants.length + 1 }]
    }));
  };

  const updateVariant = (index, field, value) => {
    setPackageForm(prev => ({
      ...prev,
      variants: prev.variants.map((v, i) => i === index ? { ...v, [field]: value } : v)
    }));
  };

  const removeVariant = (index) => {
    setPackageForm(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index)
    }));
  };

  const addMatrixRow = () => {
    setPackageForm(prev => ({
      ...prev,
      matrix_rows: [...prev.matrix_rows, { id: crypto.randomUUID(), story_type: 'single', bed_bath_tier: '', price_ex_gst: 0, sort_order: prev.matrix_rows.length + 1 }]
    }));
  };

  const updateMatrixRow = (index, field, value) => {
    setPackageForm(prev => ({
      ...prev,
      matrix_rows: prev.matrix_rows.map((r, i) => i === index ? { ...r, [field]: value } : r)
    }));
  };

  const removeMatrixRow = (index) => {
    setPackageForm(prev => ({
      ...prev,
      matrix_rows: prev.matrix_rows.filter((_, i) => i !== index)
    }));
  };

  const addChecklistItem = () => {
    setPackageForm(prev => ({
      ...prev,
      checklist_items: [...prev.checklist_items, { id: crypto.randomUUID(), task: '', group: 'General', sort_order: prev.checklist_items.length + 1, required: true, photo_required: false }]
    }));
  };

  const updateChecklistItem = (index, field, value) => {
    setPackageForm(prev => ({
      ...prev,
      checklist_items: prev.checklist_items.map((item, i) => i === index ? { ...item, [field]: value } : item)
    }));
  };

  const removeChecklistItem = (index) => {
    setPackageForm(prev => ({
      ...prev,
      checklist_items: prev.checklist_items.filter((_, i) => i !== index)
    }));
  };

  const getChecklistGroups = () => {
    const groups = {};
    packageForm.checklist_items.forEach(item => {
      if (!groups[item.group]) groups[item.group] = [];
      groups[item.group].push(item);
    });
    return groups;
  };

  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-lg font-semibold text-zinc-900">Service Packages</h2>
          <p className="text-sm text-zinc-500">Manage cleaning packages with pricing and checklists</p>
        </div>
        <Button onClick={() => initializePackageForm()} data-testid="add-package-btn">
          <Plus className="mr-2 h-4 w-4" />
          Add Package
        </Button>
      </div>
      
      <div className="grid gap-4">
        {servicePackages.map((pkg) => (
          <Card key={pkg.id} className={!pkg.active ? 'opacity-60' : ''}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Package className="h-5 w-5 text-zinc-400" />
                    <h3 className="font-semibold text-zinc-900">{pkg.name}</h3>
                    <Badge className={pkg.active ? 'bg-emerald-50 text-emerald-700' : 'bg-zinc-100 text-zinc-600'}>
                      {pkg.active ? 'Active' : 'Inactive'}
                    </Badge>
                    <Badge className="bg-blue-50 text-blue-700">
                      {getPricingModelLabel(pkg.pricing_model)}
                    </Badge>
                  </div>
                  {pkg.description && (
                    <p className="text-sm text-zinc-500 mb-3">{pkg.description}</p>
                  )}
                  
                  {/* Pricing Display */}
                  <div className="bg-zinc-50 rounded-lg p-3 mb-3">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="h-4 w-4 text-zinc-500" />
                      <span className="font-medium text-sm text-zinc-700">Pricing</span>
                    </div>
                    {pkg.pricing_model === 'flat' && (
                      <p className="text-lg font-semibold text-zinc-900">
                        {formatPrice(pkg.flat_price_ex_gst)} <span className="text-sm font-normal text-zinc-500">ex GST</span>
                      </p>
                    )}
                    {pkg.pricing_model === 'variant' && pkg.variants && (
                      <div className="space-y-1">
                        {pkg.variants.map(v => (
                          <div key={v.id} className="flex justify-between text-sm">
                            <span className="text-zinc-600">{v.name}</span>
                            <span className="font-medium text-zinc-900">{formatPrice(v.price_ex_gst)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {pkg.pricing_model === 'matrix' && pkg.matrix_rows && (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-zinc-500">
                              <th className="text-left py-1">Type</th>
                              <th className="text-left py-1">Bed/Bath</th>
                              <th className="text-right py-1">Price</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pkg.matrix_rows.slice(0, 4).map(r => (
                              <tr key={r.id}>
                                <td className="py-1 capitalize text-zinc-600">{r.story_type}</td>
                                <td className="py-1 text-zinc-600">{r.bed_bath_tier}</td>
                                <td className="py-1 text-right font-medium text-zinc-900">{formatPrice(r.price_ex_gst)}</td>
                              </tr>
                            ))}
                            {pkg.matrix_rows.length > 4 && (
                              <tr>
                                <td colSpan="3" className="py-1 text-zinc-400 text-center">
                                  +{pkg.matrix_rows.length - 4} more rows
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                  
                  {/* Checklist Summary */}
                  <div className="flex items-center gap-4 text-sm text-zinc-500">
                    <div className="flex items-center gap-1">
                      <ClipboardList className="h-4 w-4" />
                      <span>{pkg.checklist_items?.length || 0} checklist items</span>
                    </div>
                    {pkg.optional_extras && pkg.optional_extras.length > 0 && (
                      <span>• {pkg.optional_extras.length} optional extras</span>
                    )}
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-2 ml-4">
                  <Button variant="ghost" size="sm" onClick={() => handleTogglePackageActive(pkg)} title={pkg.active ? 'Deactivate' : 'Activate'}>
                    {pkg.active ? <XCircle className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => initializePackageForm(pkg)} data-testid={`edit-package-${pkg.id}`}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDeletePackage(pkg.id)} data-testid={`delete-package-${pkg.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {servicePackages.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center text-zinc-500">
              <Package className="h-12 w-12 mx-auto mb-3 text-zinc-300" />
              <p>No service packages yet</p>
              <p className="text-sm">Add packages to standardize your services and pricing</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Service Package Dialog */}
      <Dialog open={showPackageDialog} onOpenChange={setShowPackageDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPackage ? 'Edit Service Package' : 'New Service Package'}</DialogTitle>
            <DialogDescription>Configure pricing models and checklist templates</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid gap-4">
              <div>
                <Label htmlFor="pkg-name">Package Name *</Label>
                <Input id="pkg-name" value={packageForm.name} onChange={(e) => setPackageForm(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g., General Clean" data-testid="package-name-input" />
              </div>
              <div>
                <Label htmlFor="pkg-description">Description</Label>
                <Textarea id="pkg-description" value={packageForm.description} onChange={(e) => setPackageForm(prev => ({ ...prev, description: e.target.value }))} placeholder="Brief description of this package" rows={2} />
              </div>
            </div>
            
            {/* Pricing Model */}
            <div>
              <Label>Pricing Model</Label>
              <Select value={packageForm.pricing_model} onValueChange={(value) => setPackageForm(prev => ({ ...prev, pricing_model: value }))}>
                <SelectTrigger data-testid="pricing-model-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="flat">Flat Price - Single fixed price</SelectItem>
                  <SelectItem value="variant">Variant - Multiple options (e.g., crew size)</SelectItem>
                  <SelectItem value="matrix">Matrix - Based on property size (bed/bath/story)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Flat Pricing */}
            {packageForm.pricing_model === 'flat' && (
              <div>
                <Label htmlFor="flat-price">Price (ex GST)</Label>
                <div className="flex items-center gap-2">
                  <span className="text-zinc-500">$</span>
                  <Input id="flat-price" type="number" step="0.01" value={packageForm.flat_price_ex_gst} onChange={(e) => setPackageForm(prev => ({ ...prev, flat_price_ex_gst: parseFloat(e.target.value) || 0 }))} className="max-w-[150px]" data-testid="flat-price-input" />
                </div>
              </div>
            )}
            
            {/* Variant Pricing */}
            {packageForm.pricing_model === 'variant' && (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <Label>Price Variants</Label>
                  <Button variant="outline" size="sm" onClick={addVariant}><Plus className="h-4 w-4 mr-1" /> Add Variant</Button>
                </div>
                <div className="space-y-2">
                  {packageForm.variants.map((variant, idx) => (
                    <div key={variant.id} className="flex items-center gap-2 p-2 border rounded-lg bg-zinc-50">
                      <GripVertical className="h-4 w-4 text-zinc-400" />
                      <Input value={variant.name} onChange={(e) => updateVariant(idx, 'name', e.target.value)} placeholder="Variant name (e.g., Single Cleaner)" className="flex-1" />
                      <div className="flex items-center gap-1">
                        <span className="text-zinc-500">$</span>
                        <Input type="number" step="0.01" value={variant.price_ex_gst} onChange={(e) => updateVariant(idx, 'price_ex_gst', parseFloat(e.target.value) || 0)} className="w-24" />
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeVariant(idx)} disabled={packageForm.variants.length <= 1}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Matrix Pricing */}
            {packageForm.pricing_model === 'matrix' && (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <Label>Price Matrix</Label>
                  <Button variant="outline" size="sm" onClick={addMatrixRow}><Plus className="h-4 w-4 mr-1" /> Add Row</Button>
                </div>
                <div className="space-y-2">
                  {packageForm.matrix_rows.map((row, idx) => (
                    <div key={row.id} className="flex items-center gap-2 p-2 border rounded-lg bg-zinc-50">
                      <Select value={row.story_type} onValueChange={(value) => updateMatrixRow(idx, 'story_type', value)}>
                        <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="single">Single</SelectItem>
                          <SelectItem value="double">Double</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input value={row.bed_bath_tier} onChange={(e) => updateMatrixRow(idx, 'bed_bath_tier', e.target.value)} placeholder="e.g., 3 Bed / 2 Bath" className="flex-1" />
                      <div className="flex items-center gap-1">
                        <span className="text-zinc-500">$</span>
                        <Input type="number" step="0.01" value={row.price_ex_gst} onChange={(e) => updateMatrixRow(idx, 'price_ex_gst', parseFloat(e.target.value) || 0)} className="w-24" />
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeMatrixRow(idx)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                  {packageForm.matrix_rows.length === 0 && (
                    <p className="text-sm text-zinc-500 text-center py-4">Add rows for different property configurations</p>
                  )}
                </div>
              </div>
            )}
            
            {/* Checklist Items */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Label className="flex items-center gap-2">
                  <ClipboardList className="h-4 w-4" />
                  Checklist Items
                </Label>
                <Button variant="outline" size="sm" onClick={addChecklistItem}><Plus className="h-4 w-4 mr-1" /> Add Item</Button>
              </div>
              
              {packageForm.checklist_items.length > 0 ? (
                <div className="border rounded-lg divide-y">
                  {Object.entries(getChecklistGroups()).map(([group, items]) => (
                    <div key={group}>
                      <button className="w-full flex items-center justify-between p-3 hover:bg-zinc-50 text-left" onClick={() => toggleGroup(group)}>
                        <span className="font-medium text-zinc-700">{group}</span>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{items.length} items</Badge>
                          {expandedGroups[group] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </div>
                      </button>
                      {expandedGroups[group] && (
                        <div className="bg-zinc-50 p-2 space-y-2">
                          {items.map((item) => {
                            const idx = packageForm.checklist_items.findIndex(i => i.id === item.id);
                            return (
                              <div key={item.id} className="flex items-center gap-2 p-2 bg-white rounded border">
                                <Input value={item.task} onChange={(e) => updateChecklistItem(idx, 'task', e.target.value)} placeholder="Task description" className="flex-1" />
                                <Input value={item.group} onChange={(e) => updateChecklistItem(idx, 'group', e.target.value)} placeholder="Group" className="w-32" />
                                <label className="flex items-center gap-1 text-xs whitespace-nowrap">
                                  <input type="checkbox" checked={item.required} onChange={(e) => updateChecklistItem(idx, 'required', e.target.checked)} className="rounded" />
                                  Required
                                </label>
                                <label className="flex items-center gap-1 text-xs whitespace-nowrap text-blue-600" title="Photo required for this item">
                                  <input type="checkbox" checked={item.photo_required || false} onChange={(e) => updateChecklistItem(idx, 'photo_required', e.target.checked)} className="rounded accent-blue-500" />
                                  Photo
                                </label>
                                <Button variant="ghost" size="sm" onClick={() => removeChecklistItem(idx)}>
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="border rounded-lg p-4 text-center text-zinc-500">
                  <ClipboardList className="h-8 w-8 mx-auto mb-2 text-zinc-300" />
                  <p className="text-sm">No checklist items yet</p>
                  <p className="text-xs">Add items that staff must complete during this service</p>
                </div>
              )}
            </div>
            
            {/* Notes */}
            <div>
              <Label htmlFor="pkg-notes">Notes</Label>
              <Textarea id="pkg-notes" value={packageForm.notes} onChange={(e) => setPackageForm(prev => ({ ...prev, notes: e.target.value }))} placeholder="e.g., Pest and carpet cleaning not included" rows={2} />
            </div>

            {/* Completion Requirements */}
            <div className="bg-zinc-50 rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base font-medium">Require Checklist Completion</Label>
                  <p className="text-sm text-zinc-500 mt-1">Staff must complete all required checklist items before marking the job as done</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={packageForm.require_checklist_completion} onChange={(e) => setPackageForm(prev => ({ ...prev, require_checklist_completion: e.target.checked }))} className="sr-only peer" data-testid="require-checklist-toggle" />
                  <div className="w-11 h-6 bg-zinc-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-zinc-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-zinc-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                </label>
              </div>
              
              {/* Require Item Photos Toggle */}
              <div className="flex items-center justify-between pt-3 border-t border-zinc-200">
                <div>
                  <Label className="text-base font-medium">Require Item Photos</Label>
                  <p className="text-sm text-zinc-500 mt-1">Enforce photo uploads for checklist items marked as "photo required"</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={packageForm.require_item_photos} onChange={(e) => setPackageForm(prev => ({ ...prev, require_item_photos: e.target.checked }))} className="sr-only peer" data-testid="require-item-photos-toggle" />
                  <div className="w-11 h-6 bg-zinc-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-zinc-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-zinc-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                </label>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPackageDialog(false)}>Cancel</Button>
            <Button onClick={handleSavePackage} disabled={creating || !packageForm.name.trim()} data-testid="save-package-btn">
              {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingPackage ? 'Save Changes' : 'Create Package'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PackagesTab;
